/**
 * `<ThemeEditor>` — modal editor for a single user-authored theme.
 *
 * 2026-06-01 (Chris ask): "per color full editability." Renders one color
 * picker per curated editable token, grouped by section (Surfaces / Text /
 * Structure / Accents / Status). Live-previews changes inline by writing the
 * draft tokens to `<html>` style.setProperty as the user picks; reverts on
 * cancel.
 *
 * Color picker uses the native `<input type="color">` for HEX, plus a free-
 * text field accepting any CSS color (oklch, hsl, rgb, named) so a power user
 * can paste in OKLCH values that the built-in seeds use.
 */

import { useEffect, useRef, useState } from 'react';
import {
  EDITABLE_TOKENS,
  type EditableTokenKey,
} from '@/theme/seeds';
import type { CustomTheme } from '@/types/wire';

export interface ThemeEditorProps {
  /** Existing theme to edit (null = create-new mode; in that case `seed` + `name` are required). */
  initial: CustomTheme | null;
  /** Required when `initial` is null — the starting token map (from a seed). */
  initialTokens: Record<EditableTokenKey, string> | null;
  /** Seed theme id (informational; used when creating a new theme). */
  seedThemeId: string;
  /** Optional initial name when creating (e.g. "Ember (mine)"). */
  initialName?: string;
  onSave: (name: string, tokens: Record<EditableTokenKey, string>) => Promise<void> | void;
  onCancel: () => void;
}

/** Group the curated tokens by their section heading for the editor layout. */
function groupTokens(): { group: string; tokens: typeof EDITABLE_TOKENS }[] {
  const seen = new Map<string, typeof EDITABLE_TOKENS[number][]>();
  for (const t of EDITABLE_TOKENS) {
    const arr = seen.get(t.group) ?? [];
    arr.push(t);
    seen.set(t.group, arr);
  }
  return [...seen.entries()].map(([group, tokens]) => ({
    group,
    tokens: tokens as typeof EDITABLE_TOKENS,
  }));
}

/**
 * Resolve a CSS color value (any format the browser parses) to a hex string
 * the `<input type="color">` element can display. Goes through a hidden DIV
 * + getComputedStyle to leverage the browser's own color parser — no manual
 * oklch math needed. Returns null if the value doesn't parse.
 */
function cssColorToHex(value: string): string | null {
  if (typeof document === 'undefined') return null;
  const probe = document.createElement('div');
  probe.style.color = 'rgb(0, 0, 0)'; // baseline
  probe.style.color = value;
  if (probe.style.color === '') return null;
  document.body.appendChild(probe);
  const rgb = window.getComputedStyle(probe).color;
  document.body.removeChild(probe);
  const match = rgb.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (!match) return null;
  const toHex = (n: string): string => Number(n).toString(16).padStart(2, '0');
  return `#${toHex(match[1])}${toHex(match[2])}${toHex(match[3])}`;
}

export function ThemeEditor({
  initial,
  initialTokens,
  seedThemeId,
  initialName,
  onSave,
  onCancel,
}: ThemeEditorProps): JSX.Element {
  const [name, setName] = useState<string>(initial?.name ?? initialName ?? 'New theme');
  const [tokens, setTokens] = useState<Record<string, string>>(() => ({
    ...(initial?.tokens ?? initialTokens ?? {}),
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Live preview — write tokens to <html> on every change. Restore on unmount /
  // cancel by clearing the keys.
  const appliedKeysRef = useRef<string[]>([]);
  useEffect(() => {
    const root = document.documentElement;
    // Clear previous preview keys.
    for (const key of appliedKeysRef.current) {
      root.style.removeProperty(`--${key}`);
    }
    const newKeys: string[] = [];
    for (const [k, v] of Object.entries(tokens)) {
      if (typeof v === 'string' && v.length > 0) {
        root.style.setProperty(`--${k}`, v);
        newKeys.push(k);
      }
    }
    appliedKeysRef.current = newKeys;
    return () => {
      // Cleanup on unmount — ThemeProvider's own effect will re-stamp the
      // committed tokens on the next render. This prevents the editor's
      // draft state from leaking when the user dismisses without saving.
      for (const key of newKeys) {
        root.style.removeProperty(`--${key}`);
      }
    };
  }, [tokens]);

  const handleColorPick = (key: string, hex: string): void => {
    setTokens((prev) => ({ ...prev, [key]: hex }));
  };

  const handleTextEdit = (key: string, value: string): void => {
    setTokens((prev) => ({ ...prev, [key]: value }));
  };

  const onConfirm = async (): Promise<void> => {
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setErr('Name is required.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await onSave(trimmed, tokens as Record<EditableTokenKey, string>);
    } catch (e) {
      setErr(String(e));
      setSaving(false);
    }
  };

  return (
    <div className="theme-editor-overlay" role="dialog" aria-modal="true" aria-label="Edit theme">
      <div className="theme-editor-panel">
        <header className="theme-editor-head">
          <div>
            <div className="theme-editor-eyebrow">Theme · cloned from {seedThemeId}</div>
            <input
              className="theme-editor-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Theme name"
              maxLength={64}
              autoFocus
            />
          </div>
          <div className="theme-editor-head__actions">
            <button type="button" className="btn" onClick={onCancel} disabled={saving}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void onConfirm()}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </header>

        {err !== null && <div className="theme-editor-error">{err}</div>}

        <div className="theme-editor-body">
          {groupTokens().map(({ group, tokens: ts }) => (
            <section key={group} className="theme-editor-group">
              <h3 className="theme-editor-group__title">{group}</h3>
              {ts.map((t) => {
                const value = tokens[t.key] ?? '';
                const hex = cssColorToHex(value);
                return (
                  <div key={t.key} className="theme-editor-row">
                    <label className="theme-editor-row__label">
                      <span className="theme-editor-row__name">{t.label}</span>
                      <span className="theme-editor-row__key">--{t.key}</span>
                    </label>
                    <div className="theme-editor-row__controls">
                      <input
                        type="color"
                        className="theme-editor-row__color"
                        value={hex ?? '#000000'}
                        onChange={(e) => handleColorPick(t.key, e.target.value)}
                        title="Pick a color"
                      />
                      <input
                        type="text"
                        className="theme-editor-row__text"
                        value={value}
                        onChange={(e) => handleTextEdit(t.key, e.target.value)}
                        placeholder="oklch(0.5 0.1 60)"
                        spellCheck={false}
                      />
                    </div>
                  </div>
                );
              })}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
